/* global window, fetch, CustomFunctions */

import { apiBaseUrl } from "../auth/authConfig";
import { authHeaders, getToken } from "../auth/authDialog";

const API_URL = apiBaseUrl();

// Returned when no valid token is available. Custom functions can't open the
// login dialog themselves, so we point the user at the task pane.
const AUTH_PROMPT = "#AUTH! Open the SheetyBanks task pane and sign in";

// Institution info and SOD branch data are static for the session, so
// lookups are cached: N cells referencing the same cert/field cost one
// request, and recalcs are free. Storing the promise (not the value) also
// collapses concurrent duplicate calls when Excel evaluates a column of INFO
// cells at once. Failures are evicted so bad certs and transient errors
// retry on the next recalc.
const infoCache = new Map<string, Promise<MatrixCell>>();
const tableCache = new Map<string, Promise<TableResponse>>();

// The task pane's Refresh button re-enters formulas expecting fresh data;
// it clears the caches through this hook (separate bundle, shared window).
window.sheetybanksClearCaches = () => {
  infoCache.clear();
  tableCache.clear();
};

async function fetchBankInfo(cert: string, field: string): Promise<MatrixCell> {
  const response = await fetch(
    `${API_URL}/banks/${encodeURIComponent(cert)}/info/${encodeURIComponent(field)}`,
    { headers: authHeaders() }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error ?? response.statusText);
  }

  // Numbers (asset, offices, latitude, …) stay numeric so Excel can compute
  // on them; a null attribute is real missing data → native #N/A.
  const data: { cert: number; field: string; value: string | number | null } =
    await response.json();
  return data.value ?? naError();
}

/**
 * Gets static institution information for a bank (e.g. NAME, CITY, STALP,
 * ADDRESS, ZIP, COUNTY, WEBADDR, BKCLASS, ESTYMD, OFFICES, ASSET, DEP).
 * Accepts any glossary field marked INFO, case-insensitive.
 * No date needed — this data doesn't change quarter to quarter.
 * @version 0.3
 * @customfunction SHEETYBANKS.INFO
 * @param {string} cert FDIC certificate number
 * @param {string} field Attribute to retrieve — any glossary INFO field, e.g. NAME, CITY, STALP, WEBADDR, ASSET
 * @returns {Promise<any>} The requested value
 */
// Returns Promise<any> (not MatrixCell) — the custom-functions metadata plugin
// reads the signature and only supports primitive types.
export async function getBankInfo(cert: string, field: string): Promise<any> {
  if (!getToken()) return AUTH_PROMPT;

  const key = `${String(cert).trim()}|${String(field).trim().toLowerCase()}`;
  let pending = infoCache.get(key);
  if (!pending) {
    pending = fetchBankInfo(String(cert).trim(), String(field).trim().toLowerCase());
    infoCache.set(key, pending);
  }

  try {
    return await pending;
  } catch (error) {
    infoCache.delete(key);
    return `#ERROR: ${error.message}`;
  }
}

// --- SHEETYBANKS.METRICS (matrix) helpers ----------------------------------

type MatrixCell = number | string | CustomFunctions.Error;

// Real #N/A error when the runtime supports it (IFNA/ISNA-compatible),
// otherwise the display string.
function naError(): MatrixCell {
  if (typeof CustomFunctions !== "undefined" && CustomFunctions.Error) {
    return new CustomFunctions.Error(CustomFunctions.ErrorCode.notAvailable);
  }
  return "#N/A";
}

function valueError(message: string): MatrixCell {
  if (typeof CustomFunctions !== "undefined" && CustomFunctions.Error) {
    return new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, message);
  }
  return `#ERROR: ${message}`;
}

// Normalizes a header cell: null/blank → "", everything else → trimmed string.
function cellText(value: unknown): string {
  return value == null ? "" : String(value).trim();
}

// Accepts a range shaped 1×N or N×1 and returns it as a flat list.
function flattenVector(range: unknown[][], name: string): unknown[] {
  if (!Array.isArray(range) || range.length === 0 || !Array.isArray(range[0])) {
    throw new Error(`${name} must be a range`);
  }
  if (range.length === 1) {
    return range[0];
  }
  if (range.every((row) => row.length === 1)) {
    return range.map((row) => row[0]);
  }
  throw new Error(`${name} must be a single row or a single column`);
}

/**
 * Fills a matrix of metrics for multiple banks × multiple field/quarter columns
 * in one call. Place it in the top-left cell of the data region; results spill
 * down (one row per cert) and right (one column per field). Pair each field with
 * a quarter via a second header row — a single quarter cell applies to every
 * field, and omitting quarters returns the latest value of each field.
 * @version 0.1
 * @customfunction SHEETYBANKS.METRICS
 * @param {any[][]} certs Range of FDIC certificate numbers (single row or column)
 * @param {any[][]} fields Range of FDIC field names, e.g. ROA, ASSET (single row or column)
 * @param {any[][]} [quarters] Range of quarters in YYYYQN format matching fields, or one cell for all; omit for latest
 * @returns {Promise<any[][]>} Matrix of values (#N/A where no data exists)
 */
export async function getBankMetricsMatrix(
  certs: any[][],
  fields: any[][],
  quarters?: any[][]
): Promise<any[][]> {
  if (!getToken()) return [[AUTH_PROMPT]];

  let certList: string[];
  let fieldList: string[];
  let quarterList: string[];
  try {
    certList = flattenVector(certs, "certs").map(cellText);
    fieldList = flattenVector(fields, "fields").map(cellText);
    quarterList =
      quarters == null
        ? fieldList.map(() => "")
        : flattenVector(quarters, "quarters").map(cellText);
  } catch (error) {
    return [[`#ERROR: ${error.message}`]];
  }

  if (quarterList.length === 1 && fieldList.length > 1) {
    // Single quarter cell broadcasts across every field column.
    quarterList = fieldList.map(() => quarterList[0]);
  }
  if (quarterList.length !== fieldList.length) {
    return [
      [
        `#ERROR: quarters range (${quarterList.length}) must match fields range (${fieldList.length}) or be a single cell`,
      ],
    ];
  }

  // Blank header cells make blank output rows/columns (so ranges can be sized
  // generously); only the populated ones are sent to the API.
  const activeRows = certList.map((cert, i) => ({ cert, index: i })).filter((r) => r.cert !== "");
  const activeCols = fieldList
    .map((field, i) => ({ field, quarter: quarterList[i], index: i }))
    .filter((c) => c.field !== "");

  if (activeRows.length === 0) return [["#ERROR: certs range is empty"]];
  if (activeCols.length === 0) return [["#ERROR: fields range is empty"]];

  try {
    const response = await fetch(`${API_URL}/banks/metrics`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({
        certs: activeRows.map((r) => r.cert),
        columns: activeCols.map((c) => ({ field: c.field, quarter: c.quarter || undefined })),
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      return [[`#ERROR: ${error.error ?? response.statusText}`]];
    }

    const data: {
      columns: { field: string; quarter: string | null; error?: string }[];
      values: (number | null)[][];
    } = await response.json();

    // Reassemble the full grid in the shape of the input ranges.
    const output: MatrixCell[][] = certList.map(() => fieldList.map(() => "" as MatrixCell));
    activeRows.forEach((row, r) => {
      activeCols.forEach((col, c) => {
        const colError = data.columns[c]?.error;
        const value = data.values[r]?.[c];
        output[row.index][col.index] = colError ? valueError(colError) : (value ?? naError());
      });
    });
    return output;
  } catch (error) {
    return [[`#ERROR: ${error.message}`]];
  }
}

// --- SHEETYBANKS.BRANCHES / SHEETYBANKS.MARKET (spill tables) ---------------

// Shape shared by /banks/:cert/branches and /markets/:geo: requested columns
// echoed back (with a per-column error for unknown names) and rows of cells.
type TableResponse = {
  fields: { field: string; error?: string }[];
  rows: (string | number | null)[][];
};

// Optional numeric argument from a formula. Declared as `any` in the JSDoc
// (not `number`) so a blank referenced cell arrives as null rather than
// being coerced to 0; omitted, blank, zero and non-numeric all mean "not
// given".
function optionalNumber(value: unknown): number | undefined {
  if (value == null || value === "") return undefined;
  const n = Number(value);
  return isNaN(n) || n === 0 ? undefined : n;
}

async function fetchTable(url: string): Promise<TableResponse> {
  const response = await fetch(url, { headers: authHeaders() });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error ?? response.statusText);
  }
  return (await response.json()) as TableResponse;
}

function cachedTable(url: string): Promise<TableResponse> {
  let pending = tableCache.get(url);
  if (!pending) {
    pending = fetchTable(url);
    tableCache.set(url, pending);
    pending.catch(() => tableCache.delete(url));
  }
  return pending;
}

// Builds the spilled grid for a table endpoint. Blank header cells become
// blank output columns (so ranges can be sized generously); only populated
// names are requested. A table with no rows spills a single #N/A.
async function spillTable(
  fields: any[][],
  buildUrl: (fieldNames: string[]) => string
): Promise<any[][]> {
  if (!getToken()) return [[AUTH_PROMPT]];

  let fieldList: string[];
  try {
    fieldList = flattenVector(fields, "fields").map(cellText);
  } catch (error) {
    return [[`#ERROR: ${error.message}`]];
  }
  const activeCols = fieldList
    .map((field, index) => ({ field, index }))
    .filter((c) => c.field !== "");
  if (activeCols.length === 0) return [["#ERROR: fields range is empty"]];

  try {
    const data = await cachedTable(buildUrl(activeCols.map((c) => c.field)));
    if (data.rows.length === 0) return [[naError()]];

    return data.rows.map((row) => {
      const output: MatrixCell[] = fieldList.map(() => "" as MatrixCell);
      activeCols.forEach((col, c) => {
        const colError = data.fields[c]?.error;
        const value = row[c];
        output[col.index] = colError ? valueError(colError) : (value ?? naError());
      });
      return output;
    });
  } catch (error) {
    return [[`#ERROR: ${error.message}`]];
  }
}

/**
 * Lists a bank's branch offices from the FDIC Summary of Deposits survey.
 * Place it in the top-left cell; results spill down (one row per branch, main
 * office first) and right (one column per field). Fields are the glossary
 * BRANCHES names, e.g. NAMEBR, ADDRESBR, CITYBR, STALPBR, ZIPBR, CNTYNAMB,
 * DEPSUMBR (thousands USD), SIMS_LATITUDE, SIMS_LONGITUDE, BRSERTYP (service
 * type code) or BRSERTYP_DESC (its label). Limited-service offices often
 * report 0 deposits because their accounts are booked to another office.
 * @version 0.1
 * @customfunction SHEETYBANKS.BRANCHES
 * @param {string} cert FDIC certificate number
 * @param {any[][]} fields Range of branch field names (single row or column), e.g. NAMEBR, CITYBR, DEPSUMBR
 * @param {any} [year] Survey year, e.g. 2024; omit (or leave the cell blank) for the bank's most recent survey
 * @returns {Promise<any[][]>} One row per branch (#N/A if the bank has no branches)
 */
export async function getBankBranches(cert: string, fields: any[][], year?: any): Promise<any[][]> {
  const certText = String(cert).trim();
  const y = optionalNumber(year);
  return spillTable(
    fields,
    (names) =>
      `${API_URL}/banks/${encodeURIComponent(certText)}/branches?fields=${encodeURIComponent(names.join(","))}` +
      (y === undefined ? "" : `&year=${y}`)
  );
}

/**
 * Ranks every institution in a deposit market by Summary of Deposits branch
 * deposits. Place it in the top-left cell; results spill down (one row per
 * institution, largest first) and right (one column per field). The market
 * is a state ("TX"), a 5-digit county FIPS code ("48113"), "msa:19100", or
 * "zip:75201" — find codes on the Markets tab. Fields: RANK, CERT, NAME,
 * HOLDINGCO, BRANCHES, DEPOSITS, SHARE, BRANCHES_PY, DEPOSITS_PY, SHARE_PY,
 * GROWTH (deposits in thousands USD; SHARE and GROWTH in percent).
 * @version 0.1
 * @customfunction SHEETYBANKS.MARKET
 * @param {string} market State code, county FIPS, msa:code, or zip:code
 * @param {any[][]} fields Range of column names (single row or column), e.g. RANK, NAME, DEPOSITS, SHARE
 * @param {any} [year] Survey year, e.g. 2024; omit (or leave the cell blank) for the latest survey
 * @param {any} [top] Only the largest N institutions; omit for all (max 500)
 * @returns {Promise<any[][]>} One row per institution in the market
 */
export async function getMarketShare(
  market: string,
  fields: any[][],
  year?: any,
  top?: any
): Promise<any[][]> {
  const geo = String(market).trim();
  const y = optionalNumber(year);
  const n = optionalNumber(top);
  return spillTable(
    fields,
    (names) =>
      `${API_URL}/markets/${encodeURIComponent(geo)}?fields=${encodeURIComponent(names.join(","))}` +
      (y === undefined ? "" : `&year=${y}`) +
      (n === undefined ? "" : `&top=${n}`)
  );
}

CustomFunctions.associate("SHEETYBANKS.INFO", getBankInfo);
CustomFunctions.associate("SHEETYBANKS.METRICS", getBankMetricsMatrix);
CustomFunctions.associate("SHEETYBANKS.BRANCHES", getBankBranches);
CustomFunctions.associate("SHEETYBANKS.MARKET", getMarketShare);